import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, User, Calendar, MessageCircle, AlertTriangle, Archive } from "lucide-react";
import { format } from "date-fns";
import { JobMessagingDialog } from "./JobMessagingDialog";

interface ChatConversation {
  job_id: string;
  job_title: string;
  customer_id: string;
  customer_name: string;
  last_message_at: string;
  unread_count: number;
}

interface ChatInboxDialogProps {
  trigger?: React.ReactNode;
  showUnreadCount?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onUnreadCountChange?: (count: number) => void;
}

export const ChatInboxDialog = ({ trigger, showUnreadCount = true, open: externalOpen, onOpenChange, onUnreadCountChange }: ChatInboxDialogProps) => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<ChatConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<ChatConversation | null>(null);
  const [open, setOpen] = useState(externalOpen || false);
  const [error, setError] = useState<string | null>(null);
  const [myProfileId, setMyProfileId] = useState<string | null>(null);

  const markAllUnreadAsRead = async () => {
    try {
      // Resolve current user's profile id
      let profileId = myProfileId;
      if (!profileId) {
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!profile?.id) return;
        profileId = profile.id;
        setMyProfileId(profile.id);
      }

      // Bulk mark all unread messages for this recipient as read
      const { error: updateError } = await supabase
        .from("messages")
        .update({ is_read_by_recipient: true })
        .eq("recipient_id", profileId)
        .eq("is_read_by_recipient", false);
      if (updateError) {
        console.warn("Failed to bulk mark messages read:", updateError.message);
      }
    } catch (e) {
      console.warn("markAllUnreadAsRead error:", e);
    }
  };
  const fetchConversations = async () => {
    if (isLoading) return; // Prevent multiple simultaneous calls
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Ensure we have an authenticated user first
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      
      const user = authData?.user;
      if (!user) {
        setConversations([]);
        return;
      }

      // Get current profile id with better error handling
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, user_type")
        .eq("user_id", user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile?.id || profile.user_type !== 'tradesman') {
        console.log('Not a tradesman or profile not found');
        setConversations([]);
        return;
      }

      // Get all messages where this tradesman is either sender or recipient
      const { data: inboxMessages, error: inboxError } = await supabase
        .from("messages")
        .select("job_id, sender_id, recipient_id, created_at, is_read_by_recipient")
        .or(`recipient_id.eq.${profile.id},sender_id.eq.${profile.id}`)
        .order("created_at", { ascending: false });

      if (inboxError) throw inboxError;

      const messages = inboxMessages || [];
      const jobIds = Array.from(new Set(messages.map((m) => m.job_id)));

      if (jobIds.length === 0) {
        setConversations([]);
        return;
      }

      // Fetch job details in bulk with error handling
      const { data: jobsData, error: jobsError } = await supabase
        .from("jobs")
        .select("id, title, customer_id")
        .in("id", jobIds);
      
      if (jobsError) throw jobsError;

      const jobsById = new Map((jobsData || []).map((j) => [j.id, j]));

      // Fetch customer profiles in bulk with error handling
      const customerIds = Array.from(new Set((jobsData || []).map((j) => j.customer_id).filter(Boolean)));
      let customerProfilesById = new Map<string, any>();
      
      if (customerIds.length > 0) {
        const { data: customerProfiles, error: customerError } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", customerIds);
        
        if (customerError) {
          console.warn('Error fetching customer profiles:', customerError);
          // Continue without customer names rather than failing
        } else {
          customerProfilesById = new Map((customerProfiles || []).map((p) => [p.id, p]));
        }
      }

      // Get archived jobs for this tradesman
      const { data: archivedJobs } = await supabase
        .from("chat_archives")
        .select("job_id")
        .eq("archived_by", profile.id);
      
      const archivedJobIds = new Set(archivedJobs?.map(a => a.job_id) || []);

      // Compute unread counts and last message per job from the messages we already fetched
      const countsByJob = new Map<string, number>();
      const lastByJob = new Map<string, string>();
      for (const m of messages) {
        // Only count unread messages where this tradesman is the recipient (not messages they sent)
        if (!m.is_read_by_recipient && m.recipient_id === profile.id) {
          countsByJob.set(m.job_id, (countsByJob.get(m.job_id) || 0) + 1);
        }
        if (!lastByJob.has(m.job_id)) {
          lastByJob.set(m.job_id, m.created_at);
        }
      }

      // Build conversations array with fallback values
      const allConversations: ChatConversation[] = jobIds
        .map((jobId) => {
          const job = jobsById.get(jobId);
          if (!job) return null;
          
          const customerProfile = customerProfilesById.get(job.customer_id);
          const fullName = customerProfile?.full_name || customerProfile?.email || "Customer";
          const firstName = typeof fullName === 'string' && fullName.includes(' ') ? fullName.split(' ')[0] : fullName;
          
          return {
            job_id: jobId,
            job_title: job.title || 'Untitled Job',
            customer_id: job.customer_id,
            customer_name: firstName,
            last_message_at: lastByJob.get(jobId) || new Date(0).toISOString(),
            unread_count: countsByJob.get(jobId) || 0,
          } as ChatConversation;
        })
        .filter(Boolean) as ChatConversation[];

      // Sort by last message desc
      allConversations.sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime());

      // Separate active and archived conversations
      const activeConversations = allConversations.filter(conv => !archivedJobIds.has(conv.job_id));
      const archivedConversations = allConversations.filter(conv => archivedJobIds.has(conv.job_id));

      setConversations(activeConversations);
      setArchivedConversations(archivedConversations);
    } catch (error: any) {
      console.error("Error fetching conversations:", error);
      setError(error.message || 'Failed to load conversations');
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch conversations on mount for initial count, then when dialog opens
  useEffect(() => {
    fetchConversations();
  }, []);

useEffect(() => {
  if (open) {
    onUnreadCountChange?.(0);
    markAllUnreadAsRead();
    fetchConversations();
  }
}, [open]);

  // Set up realtime subscription for message updates
  useEffect(() => {
    const channel = supabase
      .channel('inbox-messages-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages'
      }, (payload) => {
        // Refresh when messages are marked as read
        if (payload.new.is_read_by_recipient !== payload.old.is_read_by_recipient) {
          fetchConversations();
        }
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages'
      }, () => {
        // Refresh when new messages arrive
        fetchConversations();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Handle external open prop changes
  useEffect(() => {
    if (externalOpen !== undefined) {
      setOpen(externalOpen);
    }
  }, [externalOpen]);

  const getTotalUnreadCount = () => {
    return conversations.reduce((total, conv) => total + conv.unread_count, 0);
  };

// Update parent with unread zero while inbox open or a conversation is active
useEffect(() => {
  const totalUnread = getTotalUnreadCount();
  if (open || selectedConversation) {
    onUnreadCountChange?.(0);
  } else {
    onUnreadCountChange?.(totalUnread);
  }
}, [conversations, onUnreadCountChange, open, selectedConversation]);

  const handleConversationClick = (conversation: ChatConversation) => {
    setSelectedConversation(conversation);
    setOpen(false);
    // Keep parent state in sync so the button can reopen the inbox later
    onOpenChange?.(false);
  };

  const handleMessagingClose = () => {
    setSelectedConversation(null);
    // Refresh conversations to update unread counts after a short delay
    // to allow the mark-as-read operation to complete
    setTimeout(() => {
      fetchConversations();
    }, 100);
  };

  const handleArchiveConversation = async (conversation: ChatConversation) => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData?.user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", authData.user.id)
        .maybeSingle();

      if (!profile?.id) return;

      const { error } = await supabase
        .from("chat_archives")
        .insert({
          job_id: conversation.job_id,
          archived_by: profile.id,
          reason: "User archived"
        });

      if (error) throw error;

      // Move conversation from active to archived
      setConversations(prev => prev.filter(c => c.job_id !== conversation.job_id));
      setArchivedConversations(prev => [...prev, conversation]);
      
    } catch (error: any) {
      console.error("Error archiving conversation:", error);
    }
  };

  const handleUnarchiveConversation = async (conversation: ChatConversation) => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData?.user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", authData.user.id)
        .maybeSingle();

      if (!profile?.id) return;

      const { error } = await supabase
        .from("chat_archives")
        .delete()
        .eq("job_id", conversation.job_id)
        .eq("archived_by", profile.id);

      if (error) throw error;

      // Move conversation from archived to active
      setArchivedConversations(prev => prev.filter(c => c.job_id !== conversation.job_id));
      setConversations(prev => [...prev, conversation]);
      
    } catch (error: any) {
      console.error("Error unarchiving conversation:", error);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="h-7 px-2 border-primary/30 relative">
      <MessageCircle className="h-3 w-3 mr-1" />
      <span className="hidden md:inline text-xs">Chat Inbox</span>
      {showUnreadCount && !open && getTotalUnreadCount() > 0 && (
        <Badge 
          variant="destructive" 
          className="absolute -top-1 -right-1 text-xs min-w-5 h-5 flex items-center justify-center p-0"
        >
          {getTotalUnreadCount()}
        </Badge>
      )}
    </Button>
  );

  return (
    <>
<Dialog open={open} onOpenChange={(newOpen) => {
  setOpen(newOpen);
  onOpenChange?.(newOpen);
  if (newOpen) onUnreadCountChange?.(0);
}}>
        {externalOpen === undefined && (
          <DialogTrigger asChild>
            {trigger || defaultTrigger}
          </DialogTrigger>
        )}
        <DialogContent className="w-[95vw] max-w-4xl max-h-[80vh] mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Chat Inbox
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="active" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="active">
                Active Conversations
                {!open && getTotalUnreadCount() > 0 && (
                  <Badge variant="destructive" className="ml-2 text-xs">
                    {getTotalUnreadCount()}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="archived">Archived</TabsTrigger>
            </TabsList>
            
            <TabsContent value="active" className="mt-4">
          <ScrollArea className="h-[500px] pr-4">
            {error ? (
              <div className="text-center py-8 text-destructive">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Failed to load conversations</p>
                <p className="text-sm mt-2">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => fetchConversations()}
                >
                  Try Again
                </Button>
              </div>
            ) : isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No conversations yet</p>
                    <p className="text-sm">Messages from customers will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {conversations.map((conversation) => (
                      <Card 
                        key={conversation.job_id} 
                        className="cursor-pointer hover:shadow-md transition-all duration-200 border-l-4 border-l-primary/20 hover:border-l-primary"
                        onClick={() => handleConversationClick(conversation)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="text-base font-medium flex items-center gap-2">
                                {conversation.job_title}
                                {conversation.unread_count > 0 && (
                                  <Badge variant="destructive" className="text-xs">
                                    {conversation.unread_count} new
                                  </Badge>
                                )}
                              </CardTitle>
                              <CardDescription className="flex items-center gap-2 mt-1">
                                <User className="h-3 w-3" />
                                {conversation.customer_name}
                                <Calendar className="h-3 w-3 ml-2" />
                                {format(new Date(conversation.last_message_at), 'MMM d, HH:mm')}
                              </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveConversation(conversation);
                                }}
                                className="h-6 w-6 p-0"
                              >
                                <Archive className="h-3 w-3" />
                              </Button>
                              <MessageCircle className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="archived" className="mt-4">
              <ScrollArea className="h-[500px] pr-4">
                {archivedConversations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No archived conversations</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {archivedConversations.map((conversation) => (
                      <Card 
                        key={conversation.job_id} 
                        className="cursor-pointer hover:shadow-md transition-all duration-200 border-l-4 border-l-muted/50 hover:border-l-muted"
                        onClick={() => handleConversationClick(conversation)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="text-base font-medium text-muted-foreground">
                                {conversation.job_title}
                              </CardTitle>
                              <CardDescription className="flex items-center gap-2 mt-1">
                                <User className="h-3 w-3" />
                                {conversation.customer_name}
                                <Calendar className="h-3 w-3 ml-2" />
                                {format(new Date(conversation.last_message_at), 'MMM d, HH:mm')}
                              </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnarchiveConversation(conversation);
                                }}
                                className="h-6 w-6 p-0"
                                title="Unarchive"
                              >
                                <Archive className="h-3 w-3" />
                              </Button>
                              <MessageCircle className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Messaging Dialog */}
      {selectedConversation && (
        <JobMessagingDialog
          job={{
            id: selectedConversation.job_id,
            title: selectedConversation.job_title,
            customer_id: selectedConversation.customer_id,
            tradesman_id: null
          }}
          recipientId={selectedConversation.customer_id}
          onClose={handleMessagingClose}
        />
      )}
    </>
  );
};